import { AnimatePresence } from "motion/react";
import type React from "react";

import logoDark from "@/assets/images/logo-dark.svg";
import { ChevronDownIcon } from "@/components/Icons/ChevronDownIcon";
import { XCloseIcon } from "@/components/Icons/XCloseIcon";
import { Overlay } from "@/components/Overlay/Overlay";
import { ProjectLogo } from "@/components/ProjectLogo/ProjectLogo";
import { ProjectSwitcher } from "@/components/ProjectSwitcher/ProjectSwitcher";
import { Sidebar } from "@/components/Sidebar/Sidebar";
import { Button, buttonVariants } from "@/design-system/Button";
import { Spinner } from "@/design-system/Spinner";
import { twResolve } from "@/helpers/tw-resolve";
import { useLinkedProjects } from "@/hooks/Network/useLinkedProjects";
import { useSessionUserOptional } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { useIsDesktop } from "@/hooks/useIsDesktop";
import { useSidebarManager } from "@/hooks/useSidebar";
import { useTranslation } from "@/translations";
import { RuntimeFloatingPortal, useRuntimeFloatingPanel } from "@/ui-adapters/RuntimeFloatingPanel";

interface ProjectSidebarHeaderProps {
  onToggleOpen: (state: boolean) => void;
}
export function ProjectSidebarHeader({ onToggleOpen }: ProjectSidebarHeaderProps): React.ReactNode {
  const [isProjectSwitcherVisible, projectSwitcherVisibilityHandlers] = useBool();
  const { t } = useTranslation();
  const [sessionUser, { isPlaceholderData }] = useSessionUserOptional({
    keepPreviousData: true,
  });
  const isDesktop = useIsDesktop();
  const { isCollapsed } = useSidebarManager();
  const connectedProjects = useLinkedProjects();
  const { panelProps } = useRuntimeFloatingPanel({
    open: isProjectSwitcherVisible,
  });
  const canSwitchProjects = connectedProjects.data && connectedProjects.data?.length > 1;

  return (
    <Sidebar.Header>
      {isDesktop && isPlaceholderData && (
        <section className="flex h-control-md items-center justify-center">
          <Spinner size="md" reveal="delayed" />
        </section>
      )}
      {isDesktop && !isPlaceholderData && (
        <section
          className={twResolve(
            "flex items-center md:h-control-md",
            isCollapsed ? "w-full justify-center px-space-sm" : "w-full justify-start px-space-lg",
          )}
        >
          <button
            type="button"
            className={twResolve(
              buttonVariants({ variant: "subtle", size: "md", fullWidth: true }),
              "h-control-xl min-h-control-md gap-space-sm rounded-lg border border-border-default bg-background-default p-space-sm",
              "enabled:hover:bg-action-subtle-primary-hover enabled:focus-visible:bg-action-subtle-primary-hover",
              "disabled:border-border-default disabled:bg-background-default disabled:text-text-primary",
              isCollapsed && "w-control-xl! justify-center p-space-xs",
            )}
            onClick={projectSwitcherVisibilityHandlers.toggle}
            disabled={!canSwitchProjects}
          >
            <span className="size-icon-display shrink-0">
              <ProjectLogo src={sessionUser?.project?.logoUrl} />
            </span>
            {!isCollapsed && (
              <>
                <span className="line-clamp-1 flex-1 text-left text-body-emphasis break-all">
                  {sessionUser?.project?.name}
                </span>
                {canSwitchProjects && <ChevronDownIcon />}
              </>
            )}
          </button>
        </section>
      )}
      {!isDesktop && (
        <header className="flex items-center justify-between px-space-md">
          <img src={logoDark} className="aspect-80/39 w-brand-mark" alt="Area of People logo" />
          <Button
            variant="ghostSecondary"
            size="md"
            shape="circular"
            inset="compact"
            aria-label={t("common.action.cancel")}
            onClick={() => onToggleOpen(false)}
          >
            <XCloseIcon size={24} />
          </Button>
        </header>
      )}
      <RuntimeFloatingPortal>
        <div className="absolute z-overlay w-full max-w-overlay-sm md:left-16!" {...panelProps}>
          <ProjectSwitcher
            isOpen={isProjectSwitcherVisible}
            onClose={projectSwitcherVisibilityHandlers.setFalse}
            showRecentProjects
            showCurrentProject={false}
          />
        </div>
        <AnimatePresence>
          {isProjectSwitcherVisible && <Overlay onClick={projectSwitcherVisibilityHandlers.setFalse} />}
        </AnimatePresence>
      </RuntimeFloatingPortal>
    </Sidebar.Header>
  );
}
